import React, { useCallback, useMemo, useState } from "react";
import { Card, Button, Nav } from "react-bootstrap";
import CustomTableWithFrontend from "../../../components/customTableWithFrontend/CustomTableWithFrontend";
import TableSearchPanel from "../../../components/tableSearchPanel/TableSearchPanel";
import { CompanyResponse } from "../../../app/models/api/company/companyResponse";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../i18n/i18n.constants";
import { useCompaniesByUserIdQuery, useDeleteCompanyMutation } from "../../../app/api/companyApi";
import { companyStatusEnumToText } from "../../../app/mappers/companyMappers";
import { ColumnType } from "../../../app/models/components/columnType";
import { FaPlus } from "react-icons/fa";
import CompanyModal from "../../modals/companies/companyModal/CompanyModal";
import ConfirmModal from "../../../components/confirmModal/ConfirmModal";
import "./MyCompanies.scss";

interface CompanyTableRow extends CompanyResponse {
    statusText: string;
}

const MyCompanies: React.FC = () => {
    const { t } = useTranslation(namespaces.userAdminPanel);
    const [selectedCompany, setSelectedCompany] = useState<CompanyResponse | null>(null);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const { data: companies, isFetching: companiesLoading } = useCompaniesByUserIdQuery();
    const [deleteCompany, { isLoading: isDeleteCompanyLoading }] = useDeleteCompanyMutation();

    const handleCompanyNameClick = useCallback(
        (company: CompanyResponse) => {
            setSelectedCompany(company);
            setShowCompanyModal(true);
        },
        []
    );

    const columns = useMemo(
        () =>
            [
                {
                    key: "companyName",
                    title: t("myCompanies.table.companyNameColumn"),
                    width: "auto",
                    render: (_item: unknown, row?: CompanyTableRow) => {
                        if (!row) return null;
                        const original = companies?.find((c) => c.id === row.id);
                        return (
                            <Nav.Link
                                className="blue-link"
                                onClick={() => original && handleCompanyNameClick(original)}
                            >
                                {row.companyName}
                            </Nav.Link>
                        );
                    },
                },
                { key: "countryCode", title: t("myCompanies.table.countryCodeColumn"), width: "auto" },
                { key: "address", title: t("myCompanies.table.addressColumn"), width: "auto" },
                { key: "zip", title: t("myCompanies.table.zipColumn"), width: "auto" },
                { key: "city", title: t("myCompanies.table.cityColumn"), width: "auto" },
                { key: "statusText", title: t("myCompanies.table.statusColumn"), width: "auto" },
                { key: "tin", title: t("myCompanies.table.tinColumn"), width: "auto" },
            ] as ColumnType[],
        [t, companies, handleCompanyNameClick]
    );

    const mappedCompanies = useMemo<CompanyTableRow[]>(() => {
        if (!companies) return [];
        return companies.map((company) => ({
            ...company,
            statusText: companyStatusEnumToText(company.status),
        }));
    }, [companies]);

    const currentTableData = useMemo(() => {
        if (searchQuery === "") return mappedCompanies;
        return mappedCompanies.filter((item: CompanyTableRow) =>
            columns.some((column) => {
                const value = item[column.key as keyof CompanyTableRow];
                return value && value.toString().toLowerCase().includes(searchQuery.toLowerCase());
            })
        );
    }, [mappedCompanies, columns, searchQuery]);

    const handleCloseCompanyModal = () => {
        setShowCompanyModal(false);
    };

    const handleDeleteFromModal = () => {
        setShowCompanyModal(false);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = useCallback(async () => {
        if (!selectedCompany) return;
        try {
            await deleteCompany(selectedCompany.id).unwrap();
        } catch (err) {
            console.error("Failed to delete company:", err);
        }
        setShowDeleteModal(false);
    }, [selectedCompany, deleteCompany]);

    const handleAddCompany = () => {
        setSelectedCompany(null);
        setShowCompanyModal(true);
    };

    const addCompanyButton = (
        <Button variant="primary" className="search-button" onClick={handleAddCompany}>
            <FaPlus className="search-button-icon" />
            <span>{t("myCompanies.buttons.addCompany")}</span>
        </Button>
    );

    return (
        <>
            {showDeleteModal && selectedCompany ? (
                <ConfirmModal
                    isShow={showDeleteModal}
                    message={t("company.deleteAlertMsg", { ns: namespaces.modals, companyName: selectedCompany.companyName })}
                    confirmLabel={t("buttons.delete", { ns: namespaces.common })}
                    confirmVariant="danger"
                    isLoading={isDeleteCompanyLoading}
                    onConfirm={handleConfirmDelete}
                    onClose={() => setShowDeleteModal(false)}
                />
            ) : undefined}
            {showCompanyModal ? (
                <CompanyModal
                    currentCompany={selectedCompany ?? undefined}
                    isShow={showCompanyModal}
                    onClose={handleCloseCompanyModal}
                    onDelete={selectedCompany && !selectedCompany.isDefault ? handleDeleteFromModal : undefined}
                />
            ) : undefined}
            <div className="my-companies mt-4">
                <h1 className="mb-4 fw-bold fs-2">{t("myCompanies.title")}</h1>
                <Card className="my-companies-content">
                    <TableSearchPanel onSearch={(s) => setSearchQuery(s)} headerAction={addCompanyButton} />
                    <CustomTableWithFrontend
                        data={currentTableData}
                        columns={columns}
                        isSearch={true}
                        isSorting={true}
                        isPagination={true}
                        isLoading={companiesLoading}
                    />
                </Card>
            </div>
        </>
    );
};

export default MyCompanies;
